import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    TextField,
    InputAdornment,
    Typography,
    IconButton,
    CircularProgress
} from '@mui/material';
import { Search as SearchIcon, Edit as EditIcon, Save as SaveIcon, X as CancelIcon } from 'lucide-react';
import { gridApi } from '../../api/grid';
import { telasApi } from '../../api/telas';
import ActionBar from '../ActionBar';
import ExportButton from '../ExportButton';
import RefreshButton from '../RefreshButton';

interface GenericEditableGridProps {
    telaId: number;
    slug: string;
    title: string;
    accentColor?: string;
}

export const GenericEditableGrid: React.FC<GenericEditableGridProps> = ({
    telaId,
    slug,
    title,
    accentColor = '#3b82f6'
}) => {
    const [data, setData] = useState<any[]>([]);
    const [metadata, setMetadata] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [editingId, setEditingId] = useState<any>(null);
    const [editValues, setEditValues] = useState<any>({});

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const [items, fields] = await Promise.all([
                    gridApi.list(slug),
                    telasApi.getCamposUsuario(telaId)
                ]);
                setData(items);
                setMetadata(fields.filter(f => f.visivel));
            } catch (error) {
                console.error('Erro ao buscar dados genéricos:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [slug, telaId]);

    const filteredData = useMemo(() => {
        if (!searchTerm) return data;
        return data.filter(item =>
            Object.values(item).some(val =>
                String(val).toLowerCase().includes(searchTerm.toLowerCase())
            )
        );
    }, [data, searchTerm]);

    const paginatedData = filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    const handleEdit = (item: any) => {
        setEditingId(item.id || item.code); // Fallback se id não existir
        setEditValues({ ...item });
    };

    const handleCancel = () => {
        setEditingId(null);
        setEditValues({});
    };

    const handleSave = async (id: any) => {
        try {
            await gridApi.update(slug, id, editValues);
            setData(prev => prev.map(item => (item.id === id || item.code === id) ? editValues : item));
            setEditingId(null);
        } catch (error) {
            console.error('Erro ao salvar alteração:', error);
        }
    };

    if (loading) return <Box p={4} display="flex" justifyContent="center"><CircularProgress style={{ color: accentColor }} /></Box>;

    return (
        <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
            <ActionBar>
                <Typography variant="h6" sx={{ color: accentColor, fontWeight: 'bold', mr: 'auto' }}>
                    {title}
                </Typography>
                <RefreshButton onClick={() => window.location.reload()} />
                <ExportButton
                    data={data}
                    columns={metadata.map(f => ({ key: f.nome_campo, label: f.nome_campo }))}
                    filename={`${slug}.xlsx`}
                    sheetName={title}
                />
            </ActionBar>

            <Paper sx={{ mt: 2, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <Box p={2}>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Filtrar nesta tabela..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><SearchIcon size={18} /></InputAdornment>
                        }}
                    />
                </Box>

                <TableContainer sx={{ flex: 1 }}>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                {metadata.map(field => (
                                    <TableCell key={field.nome_campo} sx={{ fontWeight: 'bold' }}>
                                        {field.nome_campo.toUpperCase()}
                                    </TableCell>
                                ))}
                                <TableCell align="right">AÇÕES</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedData.map((item, idx) => {
                                const isEditing = editingId === (item.id || item.code);
                                return (
                                    <TableRow key={item.id || idx} hover>
                                        {metadata.map(field => (
                                            <TableCell key={field.nome_campo}>
                                                {isEditing && field.editavel ? (
                                                    <TextField
                                                        size="small"
                                                        variant="standard"
                                                        value={editValues[field.nome_campo] ?? ''}
                                                        onChange={(e) => setEditValues({ ...editValues, [field.nome_campo]: e.target.value })}
                                                    />
                                                ) : (
                                                    String(item[field.nome_campo] ?? '-')
                                                )}
                                            </TableCell>
                                        ))}
                                        <TableCell align="right">
                                            {isEditing ? (
                                                <Box display="flex" justifyContent="flex-end" gap={1}>
                                                    <IconButton size="small" color="success" onClick={() => handleSave(item.id || item.code)}>
                                                        <SaveIcon size={18} />
                                                    </IconButton>
                                                    <IconButton size="small" color="error" onClick={handleCancel}>
                                                        <CancelIcon size={18} />
                                                    </IconButton>
                                                </Box>
                                            ) : (
                                                <IconButton size="small" color="primary" onClick={() => handleEdit(item)}>
                                                    <EditIcon size={18} />
                                                </IconButton>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    component="div"
                    count={filteredData.length}
                    page={page}
                    onPageChange={(_, p) => setPage(p)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
                />
            </Paper>
        </Box>
    );
};
